import { Input, makeStyles, mergeClasses } from '@fluentui/react-components';
import React, { PropsWithChildren } from 'react';
import { customPalette } from '../../theme';

interface IMaskTextFieldProps {
    id?: string;
    testid?: string;
    defaultValue?: string;
    className?: string;
    type?: 'number' | 'search' | 'time' | 'text' | 'email' | 'password' | 'tel' | 'url' | 'date' | 'datetime-local' | 'month' | 'week';
    prefix?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;
    value?: string;
    ariaLabel?: string;
    disableCustomStyle?: boolean;
    ariaDisabled?: boolean;
}

export const formatNumberWithCommas = (inputValue: string) => {
    const numberValue = inputValue.replace(/,/g, '');
    if (!isNaN(Number(numberValue)) && numberValue) {
        return Number(numberValue).toLocaleString();
    }
    return inputValue;
};

const useStyles = makeStyles({
    root: {
        width: '221px',
        height: '48px',
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '4px',
        paddingRight: '4px',
        borderTopLeftRadius: '6px',
        borderTopRightRadius: '6px',
        borderBottomLeftRadius: '6px',
        borderBottomRightRadius: '6px',
        boxShadow: '0px 5px 4px -4px #00000005, 0px 1px 2px 0px #0000000F',
        '@media (forced-colors: active)': {
            borderTopWidth: '1px',
            borderRightWidth: '1px',
            borderBottomWidth: '1px',
            borderLeftWidth: '1px',
            borderTopStyle: 'solid',
            borderRightStyle: 'solid',
            borderBottomStyle: 'solid',
            borderLeftStyle: 'solid',
            borderTopColor: 'WindowText',
            borderRightColor: 'WindowText',
            borderBottomColor: 'WindowText',
            borderLeftColor: 'WindowText',
            boxShadow: 'none',
            backgroundColor: 'Window',
            color: 'WindowText',
        },
    },
    prefix: {
        color: customPalette.neutralSecondary,
    },
});

const CustomMaskedTextField: React.FC<PropsWithChildren<IMaskTextFieldProps>> = ({
    testid,
    id,
    defaultValue,
    className,
    type,
    prefix,
    onChange,
    value,
    ariaLabel,
    disableCustomStyle,
    ariaDisabled = false,
}) => {
    const styles = useStyles();

    const handleInputOnChange = (event: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
        const inputValue = data.value;

        if (inputValue === undefined) {
            onChange?.(event, '');
            return;
        }

        const numberParts = inputValue.split('.');

        if (numberParts.length > 2 || (numberParts.length === 2 && numberParts[1].length > 1)) {
            return;
        }

        const formattedValue = formatNumberWithCommas(numberParts[0]) + (numberParts.length === 2 ? '.' + numberParts[1] : '');

        onChange?.(event, formattedValue);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const validKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'];
        if (!validKeys.includes(event.key)) {
            event.preventDefault();
        }
    };

    return (
        <Input
            id={id}
            type={type}
            contentBefore={prefix ? <span className={styles.prefix}>{prefix}</span> : undefined}
            onChange={handleInputOnChange}
            onKeyDown={handleKeyDown}
            className={disableCustomStyle ? className : mergeClasses(styles.root, className)}
            defaultValue={defaultValue}
            value={value}
            aria-label={ariaLabel}
            aria-disabled={ariaDisabled}
            input={testid ? { 'data-testid': testid } : undefined}
        />
    );
};

export default CustomMaskedTextField;
